import { ArrowLeft, ArrowRight, Building2, Check, FileUp, Keyboard, Link2, User, Users } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "wasp/client/auth";
import { completeOnboarding } from "wasp/client/operations";
import useTranslation from "../../client/hooks/useTranslation";
import { Button } from "../../client/components/ui/button";
import { userHasBrokerSync } from "../../payment/plans";
import { trackOnboardingCompleted, trackOnboardingShown, trackOnboardingSkipped, trackOnboardingStepCompleted, trackOnboardingStepViewed } from "../../client/utils/posthog";
import { useLiteData } from "../data/LiteDataProvider";
import { Logo } from "../shell/LiteShell";
import { useLiteActions } from "../shell/LiteActions";

type Profile = "prop" | "personal" | "both";
type Method = "broker" | "csv" | "manual";

const STEP_IDS = ["welcome", "profile", "trades"] as const;

/**
 * Premier lancement en Lite : 3 écrans, puis les vraies fenêtres (connexion
 * Tradovate, import CSV ou création de compte). Affiché tant que
 * onboardingCompletedAt est vide ET que l'utilisateur n'a ni compte ni trade.
 */
export function LiteOnboarding() {
  const t = useTranslation().lite.onboarding.flow;
  const { data: user } = useAuth();
  const { accounts, trades, loading } = useLiteData();
  const { openConnectBroker, openImportCsv, openAddAccount } = useLiteActions();
  const [dismissed, setDismissed] = useState(false);
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState<Profile>("prop");

  const done = !!(user as any)?.onboardingCompletedAt;
  const visible = !(loading || dismissed || done || accounts.length > 0 || trades.length > 0);

  // Un seul onboarding_shown par montage, puis un step_viewed par écran.
  const shownRef = useRef(false);
  useEffect(() => {
    if (!visible) return;
    if (!shownRef.current) {
      shownRef.current = true;
      trackOnboardingShown("lite", STEP_IDS[step]);
    } else {
      trackOnboardingStepViewed("lite", STEP_IDS[step]);
    }
  }, [visible, step]);

  if (!visible) return null;

  const finish = () => {
    setDismissed(true);
    completeOnboarding().catch(() => { /* non bloquant : la modale ne revient pas dans la session */ });
  };
  const skip = () => { trackOnboardingSkipped("lite", STEP_IDS[step]); finish(); };
  const choose = (method: Method, open: () => void) => {
    trackOnboardingCompleted("lite", { method, profile });
    finish();
    open();
  };
  const isMax = !!user && userHasBrokerSync(user as any);

  return (
    <div className="fixed inset-0 z-[60] bg-background overflow-y-auto">
      <div className="min-h-full flex flex-col">
        <header className="flex items-center justify-between px-6 md:px-10 h-16">
          <Logo />
          <div className="flex items-center gap-1.5" aria-label={t.step(step + 1, 3)}>
            {[0, 1, 2].map(i => <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-primary" : i < step ? "w-1.5 bg-primary/60" : "w-1.5 bg-border"}`} />)}
          </div>
          <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={skip}>{t.skip}</button>
        </header>
        <main className="flex-1 grid place-items-center px-6 pb-16">
          <div className="w-full max-w-xl" key={step}>
            {step === 0 && (
              <>
                <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{t.welcomeTitle}<span className="text-[#00C9A7]">.</span></h1>
                <p className="text-muted-foreground mt-3 text-lg">{t.welcomeLead}</p>
                <div className="mt-8 space-y-3">
                  {t.welcomePoints.map(p => (
                    <div key={p} className="flex items-center gap-3 text-sm"><span className="w-6 h-6 rounded-full bg-primary/15 text-primary grid place-items-center"><Check size={14} /></span>{p}</div>
                  ))}
                </div>
                <div className="mt-10"><Button size="lg" onClick={() => setStep(1)}>{t.continue} <ArrowRight className="h-4 w-4 ml-1.5" /></Button></div>
              </>
            )}
            {step === 1 && (
              <>
                <h1 className="font-display text-3xl font-bold tracking-tight">{t.profileTitle}</h1>
                <p className="text-muted-foreground mt-2">{t.profileLead}</p>
                <div className="mt-8 grid gap-3">
                  <Choice icon={<Building2 size={20} />} title={t.profileProp} desc={t.profilePropDesc} active={profile === "prop"} onClick={() => setProfile("prop")} />
                  <Choice icon={<User size={20} />} title={t.profilePersonal} desc={t.profilePersonalDesc} active={profile === "personal"} onClick={() => setProfile("personal")} />
                  <Choice icon={<Users size={20} />} title={t.profileBoth} desc={t.profileBothDesc} active={profile === "both"} onClick={() => setProfile("both")} />
                </div>
                <Nav back={t.back} next={t.continue} onBack={() => setStep(0)} onNext={() => { trackOnboardingStepCompleted("lite", "profile", { profile }); setStep(2); }} />
              </>
            )}
            {step === 2 && (
              <>
                <h1 className="font-display text-3xl font-bold tracking-tight">{t.tradesTitle}</h1>
                <p className="text-muted-foreground mt-2">{t.tradesLead}</p>
                <div className="mt-8 grid gap-3">
                  {profile !== "personal" && (
                    <Choice icon={<Link2 size={20} />} title={t.broker} desc={t.brokerDesc} badge={isMax ? t.brokerBadgeMax : t.brokerBadgeOther} onClick={() => choose("broker", openConnectBroker)} />
                  )}
                  <Choice icon={<FileUp size={20} />} title={t.csv} desc={t.csvDesc} onClick={() => choose("csv", openImportCsv)} />
                  <Choice icon={<Keyboard size={20} />} title={t.manual} desc={t.manualDesc} onClick={() => choose("manual", openAddAccount)} />
                </div>
                <Nav back={t.back} onBack={() => setStep(1)} />
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function Choice({ icon, title, desc, active, badge, onClick }: { icon: ReactNode; title: string; desc: string; active?: boolean; badge?: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex items-start gap-4 text-left rounded-2xl border p-4 transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}>
      <span className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${active ? "bg-primary text-primary-foreground" : "bg-muted text-primary"}`}>{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 font-semibold">{title}{badge && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary">{badge}</span>}</span>
        <span className="block text-sm text-muted-foreground mt-0.5">{desc}</span>
      </span>
      {active && <Check size={18} className="text-primary mt-1" />}
    </button>
  );
}

function Nav({ back, next, onBack, onNext }: { back: string; next?: string; onBack: () => void; onNext?: () => void }) {
  return (
    <div className="flex items-center justify-between mt-8">
      <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1.5" />{back}</Button>
      {onNext && next && <Button size="lg" onClick={onNext}>{next} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>}
    </div>
  );
}
